import { atom, useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { Fn } from 'src/common/types';
import { isLoggedIn } from 'src/common/utils/authentication';
import * as Sentry from '@sentry/react';

export const useToggleState = (defaultValue?: boolean) => {
  const [state, setState] = useState<boolean>(defaultValue ?? false);
  return { toggle: () => setState((prev) => !prev), setFalse: () => setState(false), setTrue: () => setState(true), state };
};

const authAtom = atom<boolean>(isLoggedIn());
export const useAuth = () => {
  const [auth, setAuth] = useAtom(authAtom);
  return {
    isLoggedIn: auth,
    setIsLoggedIn: (loggedIn: boolean) => setAuth(loggedIn),
  };
};

/**
 * useStableState keeps the last non-null, non-undefined value of a given input.
 *
 * When the `value` changes, `useStableState` updates its internal state only if
 * the new value is not `null` or `undefined`. If the new value is `undefined` or `null`,
 * the previous stable value is preserved.
 *
 * This is useful when you want to prevent derived state from resetting
 * due to temporarily missing values.
 *
 * @template T - The type of the value being tracked.
 * @param {T | undefined | null} value - The incoming value to watch.
 * @returns {T | undefined} The latest stable non-null, non-undefined value.
 *
 * @example
 * const [input, setInput] = useState<string | undefined>();
 * const stableValue = useStableState(input);
 */
export const useStableState = <T>(incoming: T | undefined | null): T | undefined => {
  const [stable, setStable] = useState<T | undefined>(incoming ?? undefined);
  useEffect(() => {
    if (incoming !== undefined && incoming !== null) {
      setStable(incoming);
    }
  }, [incoming]);

  return stable;
};

export const usePreload = (preloader: Fn<[], Promise<unknown>>) => {
  useEffect(() => {
    preloader().catch((error) => {
      Sentry.captureException('Failed to preload route', { extra: { error: error.message } });
    });
  }, []);
};
export const useFocusedField = <FieldName extends string>(defaultValue?: FieldName) => {
  const [focusedName, setFocusedName] = useState<FieldName | null>(defaultValue || null);
  const onFocus = (name: FieldName) => () => setFocusedName(name);
  const onBlur = () => setFocusedName(null);

  return { focusedName, onFocus, onBlur };
};
